import React, { useContext, useState, useEffect } from "react";
import InputField from "../input";
import { PrimaryButton } from "../button";
import {
  addSlashes,
  formatCardNumber,
  getAPIErrorMessage,
  handleAuthModelStage,
  formatCurrencyAmount,
} from "../../utils/helpers";
import {
  DefaultCardLogo,
  DiscoverLogo,
  MaestroLogo,
  MastercardLogo,
  VisaLogo,
} from "../icons";
import CardValidator from "card-validator";
import {
  PaymentContext,
  PaymentContextValue,
  PaymentStage,
} from "../../contexts/Payment/payment.context";
import { chargePayment, generateKey } from "../../services/api.services";
import "./index.scss";
import { usePayDataContext } from "../../contexts/PayDataContext";
import { useViewContext } from "../../contexts/view.context";
import { useUrlAuthContext } from "../../contexts/urlauth.context";
import { useUrlBodyContext } from "../../contexts/urlBody.context";

var CryptoJS = require("crypto-js");

const getCardLogo = (type: any) => {
  switch (type) {
    case "mastercard":
      return <MastercardLogo />;

    case "visa":
      return <VisaLogo />;

    case "maestro":
      return <MaestroLogo />;

    case "discover":
      return <DiscoverLogo />;

    default:
      return <DefaultCardLogo />;
  }
};

const CardDetails = () => {
  const {
    state,
    setState,
    onInputChange,
    setStage,
    setError,
    clearCard,
    setPaymentReference,
  }: PaymentContextValue = useContext(PaymentContext);

  const [loading, setLoading] = useState(false);
  const { cardNumber, cvv, expiry } = state.card;
  const card = CardValidator.number(cardNumber.replace(/\s/g, "")).card;
  const { merchantData, isLoading, payReference, randomKey, customerInfo } =
    usePayDataContext();

  const { fullName, email } = customerInfo;
  const { view, setView } = useViewContext();
  const { urlAuth, setUrlAuth } = useUrlAuthContext();
  const { urlBody, setUrlBody } = useUrlBodyContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const handleEncryption = async (rawData: string): Promise<string> => {
    const encryptionKey = await generateKey(randomKey);

    var key = atob(encryptionKey);

    rawData = CryptoJS.enc.Utf8.parse(rawData);

    const utf8key = CryptoJS.enc.Utf8.parse(key);

    var encrypted = CryptoJS.AES.encrypt(rawData, utf8key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });

    const encryptedPayload = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    return encryptedPayload;
  };

  const generateCardData = async () => {
    const formattedCardNumber = cardNumber.replace(/\s/g, "");

    if (!CardValidator.number(formattedCardNumber).isValid) {
      setError("Enter a valid card number");
      return;
    }
    if (!CardValidator.cvv(cvv).isValid) {
      setError(`Enter a valid ${card.code.name}`);
      return;
    }

    const expiryData = {
      month: expiry.split("/")[0],
      year: expiry.split("/")[1],
    };

    const payload = {
      pan: formattedCardNumber,
      cvv,
      expiry_month: expiryData.month,
      expiry_year: expiryData.year,
    };

    return await handleEncryption(JSON.stringify(payload));
  };

  const handleSubmit = async (e: any) => {
    setLoading(true);
    e.preventDefault();
    setButtonDisabled(true);
    const payload = {
      reference: `${payReference}.${randomKey}`,
      amount: merchantData?.amount,
      currency: "NGN",
      card_data: await generateCardData(),
      device_information: {
        http_browser_language: "en-US",
        http_browser_java_enabled: false,
        http_browser_javascript_enabled: "",
        http_browser_color_depth: 24,
        http_browser_screen_height: 864,
        http_browser_screen_width: 1536,
        http_browser_time_difference: "",
        user_agent_browser_value:
          "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/118.0.0.0 Safari/537.36",
        device_channel: "Browser",
        ip_address: "",
      },
      metadata: {
        name: fullName,
        email,
      },
      is_payment_link: !payReference.includes("srp") ? true : false,
    };
    setError("");
    try {
      const charge = await chargePayment(payload);
      const { auth_model, status, message } = charge;
      setState({ charge });
      let stage = PaymentStage.init;
      if (status === "Successful") {
        setStage(PaymentStage.success);
        setPaymentReference(charge.reference);
        setView("success");
        window.parent.postMessage(
          {
            action: "successful",
            data: {
              status: true,
              message: "Transaction was successful.",
              data: {
                status: "successful",
                reference: charge.reference,
              },
            },
          },
          "*"
        );

        if (merchantData.meta.redirect_url) {
          setTimeout(() => {
            window.location.href = merchantData.meta.redirect_url;
          }, 2000);
        }
        return;
      } else if (status === "Failed") {
        setError(message);
        setView("error");
      } else {
        stage = handleAuthModelStage(auth_model);
        !stage && setError(charge.message);
      }
      setStage(stage);
      setLoading(false);
      if (status === "redirect_auth") {
        setUrlAuth(charge.redirect_auth_data.url);
        setUrlBody(charge.redirect_auth_data.body, "body");
        setView("card3ds");
      }

      if (status === "send_pin") {
        setView("pin");
      }
      if (status === "send_otp") {
        setView("otp");
        return;
      }
      setLoading(false);
      clearCard();
    } catch (e) {
      setView("error");
      setLoading(false);
      clearCard();
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && e.target.name !== "cvv") {
      e.preventDefault();
      if (e.target.name === "cardNumber") {
        const nextField: any = document.querySelector(`input[name=expiry]`);
        if (nextField !== null) {
          nextField.focus();
        }
      }
      if (e.target.name === "expiry") {
        const nextField: any = document.querySelector(`input[name=cvv]`);
        if (nextField !== null) {
          nextField.focus();
        }
      }
    }
  };

  return (
    <div className="card-details">
      <p className="text-center mt-5 Inter-Semibold">
        Enter Your Card Details To Pay
      </p>
      <form className="mt-7" onSubmit={handleSubmit}>
        <div>
          <InputField
            autoComplete={"cc-number"}
            x-autocompletetype={"cc-number"}
            name={"cardNumber"}
            data-id={"field-1"}
            type="tel"
            placeholder={"1234 1234 1234 1234"}
            onChange={(e: any) => {
              const value = formatCardNumber(e.target.value);
              onInputChange({
                target: {
                  name: "cardNumber",
                  value,
                },
              });
            }}
            onKeyPress={handleKeyPress}
            title={"Card Number"}
            value={cardNumber}
            maxLength={Math.max(...(card?.lengths || [20])) + 4}
            prefix={getCardLogo(card?.type)}
          />
        </div>

        <div className="flex mt-5">
          <div className="mr-5">
            <InputField
              autoComplete={"cc-exp"}
              name={"expiry"}
              maxLength="5"
              placeholder={"MM / YY"}
              value={expiry}
              onChange={(e) => {
                const value = addSlashes(e.target.value);
                onInputChange({
                  target: {
                    name: "expiry",
                    value,
                  },
                });
              }}
              onKeyPress={handleKeyPress}
              onKeyDown={(e: any) => {
                const code = e.key || e.keyCode || e.charCode;
                if (expiry.endsWith("/") && code === "Backspace") {
                  e.preventDefault();
                  onInputChange({
                    target: {
                      name: "expiry",
                      value: expiry.substring(0, 1),
                    },
                  });
                }
              }}
              title={"Expiry Date"}
            />
          </div>
          <div>
            <InputField
              autoComplete={"cc-csc"}
              name={"cvv"}
              data-id={"field-2"}
              type={"password"}
              placeholder={"***"}
              value={cvv}
              tooltipId="cvv-tooltip"
              data-tooltip-id="cvv-tooltip"
              data-tooltip-content="The 3 digit number behind your card"
              tooltipPosition={"right"}
              onChange={onInputChange}
              maxLength={card?.code?.size || 3}
              title={card?.code?.name || "CVV"}
              onKeyPress={handleKeyPress}
              hasTooltip
            />
          </div>
        </div>
        <PrimaryButton
          text={
            isLoading
              ? "Fetching amount..."
              : `Pay ₦${formatCurrencyAmount(merchantData?.amount)}`
          }
          id="pay-button"
          className="w-full mt-8 text-lg"
          disabled={
            !cardNumber || cvv.length !== 3 || !expiry || buttonDisabled
          }
          loading={loading}
        />
      </form>
    </div>
  );
};

export default CardDetails;
